import { useState } from "react";
import { toast } from "sonner";
import { Check, Clock3, Ban, RotateCcw, History } from "lucide-react";
import { Input } from "@/components/ui/input";
import { mpatch, errMsg, fmtDate, fmtTs, todayISO, TASK_STATUS, PRIORITY } from "@/manager/lib";
import { Card, Pill, Empty } from "@/manager/ui";

function TaskRow({ t, reload, showEvent }) {
  const [hist, setHist] = useState(false);
  const [postpone, setPostpone] = useState(false);
  const patch = async (body) => { try { await mpatch(`/tasks/${t.id}`, body); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const open = ["pendiente", "en_curso"].includes(t.status);
  const overdue = open && t.due_date < todayISO();
  return (
    <div className={`py-3 border-t border-[#f0ece4] first:border-0 ${!open ? "opacity-60" : ""}`} data-testid={`task-${t.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => patch({ status: open ? "completada" : "pendiente" })} data-testid={`task-toggle-${t.id}`}
          className={`w-6 h-6 rounded-full border flex items-center justify-center ${t.status === "completada" ? "bg-emerald-600 border-emerald-600 text-white" : "border-[#c9c3b5] hover:border-emerald-600"}`}>
          {t.status === "completada" && <Check size={13} />}
        </button>
        <div className="flex-1 min-w-[180px]">
          <p className={`text-sm ${t.status === "completada" ? "line-through" : "font-medium"}`}>{t.title}</p>
          <p className="text-xs text-[#6b6a66]">
            {showEvent && t.event_title && `${t.event_title} · `}<span className={overdue ? "text-rose-600 font-medium" : ""}>vence {fmtDate(t.due_date)}</span>
            {t.assignee && ` · ${t.assignee}`} · {t.auto_key ? "automática" : "manual"}
          </p>
        </div>
        <Pill className={PRIORITY[t.priority]}>{t.priority}</Pill>
        <Pill className="bg-[#F7F5F0] text-[#6b6a66]">{TASK_STATUS[t.status]}</Pill>
        {open && <button title="En curso" onClick={() => patch({ status: "en_curso" })} data-testid={`task-progress-${t.id}`} className="text-[#6b6a66] hover:text-[#101B28]"><RotateCcw size={15} /></button>}
        {open && <button title="Posponer" onClick={() => setPostpone(!postpone)} data-testid={`task-postpone-${t.id}`} className="text-[#6b6a66] hover:text-[#101B28]"><Clock3 size={15} /></button>}
        {open && <button title="No aplica" onClick={() => patch({ status: "no_aplica", note: "marcada como no aplicable" })} data-testid={`task-na-${t.id}`} className="text-[#6b6a66] hover:text-rose-700"><Ban size={15} /></button>}
        <button title="Historial" onClick={() => setHist(!hist)} className="text-[#6b6a66]" data-testid={`task-history-${t.id}`}><History size={15} /></button>
      </div>
      {postpone && (
        <div className="flex gap-2 mt-2 ml-8">
          <Input type="date" className="w-44 h-8" defaultValue={t.due_date} onChange={(e) => e.target.value && patch({ due_date: e.target.value })} data-testid={`task-postpone-date-${t.id}`} />
          <Input placeholder="Reasignar a…" className="w-44 h-8" defaultValue={t.assignee} onBlur={(e) => e.target.value !== t.assignee && patch({ assignee: e.target.value })} data-testid={`task-assignee-${t.id}`} />
        </div>
      )}
      {hist && <ul className="mt-2 ml-8 text-xs text-[#6b6a66] space-y-0.5">{(t.history || []).map((h, i) => <li key={i}>{fmtTs(h.at)} · {h.action} · {h.by}</li>)}</ul>}
    </div>
  );
}

export default function TaskList({ tasks, reload, showEvent = false }) {
  if (!tasks.length) return <Card className="p-5"><Empty>No hay tareas.</Empty></Card>;
  return <Card className="px-5 py-2">{tasks.map((t) => <TaskRow key={t.id} t={t} reload={reload} showEvent={showEvent} />)}</Card>;
}
